import React, { useEffect, useId, useRef, useState } from "react";
import "../../css/designSystem/CustomSelect.css";

export default function CustomSelect({
  options = [],
  value = "",
  onChange,
  name = "",
  placeholder = "Please select",
  className = "",
  title = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const generatedId = useId();
  const triggerId = `${name || "custom-select"}-${generatedId.replace(/:/g, "")}`;

  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (optionValue) => {
    if (onChange) {
      onChange({
        target: {
          name,
          value: optionValue,
        },
      });
    }
    setIsOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const currentIndex = options.findIndex((option) => option.value === value);
      const direction = event.key === "ArrowDown" ? 1 : -1;
      const nextIndex = Math.min(
        Math.max(currentIndex + direction, 0),
        options.length - 1,
      );

      setIsOpen(true);
      if (options[nextIndex]) handleSelect(options[nextIndex].value);
    }
  };

  return (
    <div
      className={`custom-select-field column gap-s ${
        isOpen ? "is-open" : ""
      } ${className}`.trim()}
    >
      {title && (
        <label htmlFor={triggerId} className="content-semibold text-primary-1">
          {title}
        </label>
      )}
      <div
        ref={wrapperRef}
        className={`custom-select ${isOpen ? "is-open" : ""}`}
      >
        <button
          id={triggerId}
          type="button"
          className={`custom-select-trigger py-s px-l ${isOpen ? "open" : ""}`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onKeyDown={handleKeyDown}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span className={`body1 ${selectedOption ? "text-primary-1" : "text-gray-2"}`}>
            {selectedOption?.label || placeholder}
          </span>
          <span className={`custom-select-arrow ${isOpen ? "open" : ""}`}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </button>

        {isOpen && (
          <div className="custom-select-dropdown" role="listbox">
            {options.map((option) => {
              const isSelected = option.value === value;

              return (
                <button
                  key={option.value || option.label}
                  type="button"
                  className={`custom-select-option body1 text-primary-1 ${
                    isSelected ? "selected" : ""
                  }`.trim()}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
